import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Button, Grid, Tooltip } from '@mui/material';
import './RequestStepper.css';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { useRequestdata } from '../../useRequestData';
import STEPS, { StepList } from '../STEPS';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/store';
import { TrackGoogleAnalyticsEvent } from '../../../../shared/google-analytics';
import PreviousRequestModal from '../../../../components/Modals/PreviousRequestModal';
import { fetchRequestById, searchPreviousEquivalencyRequest } from '../../requestSlice';
import { useEffect, useMemo, useState } from 'react';
import EquivalencyRequest, { EquivalencyRequestStates } from '../../../../models/EquivalencyRequest';
import { useRecaptcha } from '../../../../shared/google-recaptcha-v3/useRecaptcha';
import googleRecaptchaActions from '../../../../shared/google-recaptcha-v3/googleRecaptchaActions';
import ConfirmationModal, { ActivityMinified } from '../../../../components/Modals/ConfirmRequestModal';
import { useRequestContext } from '../../../Request/Request.Context';
import { compareArraysByProperty } from '../../../../shared/utils/compareArrays';
import { popSnackbar } from '../../../SnackbarProvider/snackbarSlice';
import TagsModal from '../../../../components/Modals/TagsModal';
import { Simulation } from '../../../../models/Simulation';
import { Cardinality } from '../../../../models/Cardinality';
import { Proposal } from '../../../../models/Proposal';
import { useRequestType } from '../../../../shared/hooks/useRequestType';

interface RequestStepperProps {
  steps: StepList[];
  activeStep: STEPS;
  setActiveStep: (step: number) => void;
  onNext: (value: STEPS, completed?: boolean) => Promise<void>;
  onBack: (value: STEPS) => void;
  showWelcomeScreen: () => void;
  disbaleGoBack?: boolean;
}
interface StudentData {
  name: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  email: string;
  originInstitution: string;
  originPlan: string;
  targetCareer: string;
}

interface AcademicHistory {
  sourceInstitution?: string;
  sourceSyllabus?: string;
  sourceActivities?: {
    name?: string;
    id?: string;
    academic_transcript?: {
      calification?: string;
      approval_date?: string;
      folder?: string;
      book?: string;
    };
  }[];
}

export const hasUnmatchedActivities = (
  sourceActivities?: ActivityMinified[],
  rules?: Cardinality[],
  proposals?: Proposal[]
): boolean => {
  if (!sourceActivities || sourceActivities.length === 0) return true;

  return sourceActivities.some(activity => {
    const usedInRules = rules?.some(rule =>
      rule.source_activities.some(a => a.id === activity.id)
    );

    const usedInProposals = proposals?.some(
      p => p.sourceActivityId === activity.id
    );

    return !usedInRules && !usedInProposals;
  });
};

const RequestStepper: React.FC<RequestStepperProps> = ({ steps, disbaleGoBack, activeStep, setActiveStep, onNext, onBack, showWelcomeScreen }) => {
  const { t } = useTranslation();
  const {
    allActivitiesWasEnriched,
    canSaveStudentData,
    hasSelectedActivities,
    targetSyllabusWasSelected,
    hasStudyPlanAndAcademicReport,
    thirdStepValidation,
    syllabusFile,
    academicReport,
    target_syllabus: targetSyllabus,
    targetInstitution,
    termsAndConditionsChecked,
    hasDuFrontAndBackValidated,
    student,
  } = useRequestdata({});

  const requestContext = useRequestContext();
  const equivalencyRuleCheckEnabled = useAppSelector(state => state.settings.equivalencyRuleCheckEnabled);

  const { setPreviousRequestFlag } = requestContext;

  const { welcomeScreen, checkPendingRequests } = useAppSelector((state) => state.settings);
  const targetSyllabusActivities = useAppSelector(state => state.request.targetSyllabusActivities);
  const cardinality = useAppSelector(state => state.request.cardinality);
  const requestById = useAppSelector(state => state.request.requestById);
  const [continueAnyway, setContinueAnyway] = useState(false)

  const numberOfSteps = steps.length;
  const isInitialStep = activeStep === 0;
  const isLastStep = activeStep === (numberOfSteps - 1);
  const isFinalStep = activeStep === numberOfSteps;

  const lastNextStepLabel = isLastStep ? t('finish') : t('next');
  const state = useAppSelector((state) => state);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [studentData, setStudentData] = useState<StudentData | null>(null);
  const [academicHistory, setAcademicHistory] = useState<AcademicHistory>();
  const [proposals, setProposals] = useState<any[]>([]);

  const [openTagsModal, setOpenTagsModal] = useState(false);
  const [simulationData, setSimulationData] = useState<Simulation | null>(null)
  const { isExternal } = useRequestType();

  useEffect(() => {
    if (
      student
    ) {
      setStudentData({
        name: student.name,
        lastName: student.last_name,
        documentType: student.document_type,
        documentNumber: student.document_number,
        email: student.email,
        originInstitution: state.request?.simulation?.syllabus?.institution?.name ?? '',
        originPlan: state.request?.simulation?.syllabus.name ?? '',
        targetCareer: state.request?.simulation?.target_syllabus.name ?? '',
      });
    } else {
      setStudentData(null);
    }
  }, [state]);

  useEffect(() => {
    const keywords = ['Segunda carrera', 'Articulación', 'Doble titulación'];
    const tags = state.request.simulation?.tags || null
    if (tags) {
      const matchingTag = tags?.find((item: any) =>
        keywords.some(tags => item.tag.toLowerCase() === tags.toLowerCase())
      );
      if (matchingTag && !(previousPendingRequestList?.length > 0 || previousEndedRequestList?.length > 0)) {
        setOpenTagsModal(true);
        setSimulationData(state.request.simulation || null);
      }
    }

  }, [state.request.simulation]);

  useEffect(() => {
    const academicHistoryBody = {
      sourceInstitution: requestContext?.source_institution?.name,
      sourceSyllabus: requestContext?.source_syllabus?.name,
      sourceActivities: requestContext?.activities,
    }
    setAcademicHistory(academicHistoryBody);
  }, [requestContext?.source_institution, requestContext?.source_syllabus, requestContext?.activities])

  useEffect(() => {
    if (requestContext?.proposals) {
      setProposals(requestContext?.proposals);
    }
    else {
      setProposals([]);
    }

  }, [requestContext?.proposals]);

  const handleOpenConfirmationModal = () => {
    if (studentData) {
      setShowConfirmationModal(true);
    }
  }
  const handleCloseConfirmationModal = async (isConfirmed: boolean) => {
    setShowConfirmationModal(false);
    if (isConfirmed) {
      try {
        next()
      } catch (e) {
        console.error(e);
      }
    }
  };

  const hasUnmatched = useMemo(() => {
  return hasUnmatchedActivities(
    academicHistory?.sourceActivities,
    cardinality,
    proposals
  );
}, [academicHistory, cardinality, proposals]);

  const disableNextStep = () => {
    switch (activeStep) {
      case STEPS.STUDENT_DATA:
        return !canSaveStudentData;
      case STEPS.SIMULATION:
        return !targetSyllabusWasSelected
      case STEPS.ACADEMIC_HISTORY:
        return !hasSelectedActivities;
      case STEPS.TARGET_SELECTION:
        return hasUnmatched && isExternal;
      case STEPS.FINISH:
        return !termsAndConditionsChecked;
      default:
        return false;
    }
  };

  const disableStpperButton = (step: number) => {
    switch (step) {
      case STEPS.SIMULATION:
        return !canSaveStudentData;
      case STEPS.ACADEMIC_HISTORY:
        return !targetSyllabusWasSelected || !canSaveStudentData
      case STEPS.TARGET_SELECTION:
        return !hasSelectedActivities || !canSaveStudentData || !targetSyllabusWasSelected
      case STEPS.DOCUMENT_UPLOAD:
        return hasUnmatched && isExternal || !canSaveStudentData || !targetSyllabusWasSelected || !hasSelectedActivities
      case STEPS.FINISH:
        return !(
          hasDuFrontAndBackValidated &&
          academicReport &&
          syllabusFile &&
          termsAndConditionsChecked
        );
      default:
        return false;
    }
  }

  const dispatch = useAppDispatch();
  const contextValue = useRequestdata({})
  const { generateToken } = useRecaptcha(googleRecaptchaActions.requestSearch);
  const next = () => {
    setShowPreviousRequestModal(false);
    const nextStep = activeStep + 1;
    onNext(activeStep, true)
      .then(() => {
        setActiveStep(nextStep);
      }).catch((e) => {
        console.log('cached error', e);
      });
  }

  const [showPreviousRequestModal, setShowPreviousRequestModal] = useState(false);
  const [previousPendingRequestList, setPreviousPendingRequestList] = useState<EquivalencyRequest[]>([])
  const [previousEndedRequestList, setPreviousEndedRequestList] = useState<EquivalencyRequest[]>([])

  const handleClosePreviousRequestModal = (isContinue: boolean) => {
    if (!isContinue) handleBack();
    setShowPreviousRequestModal(false);
    if (isContinue) {
      setContinueAnyway(true)
      setPreviousRequestFlag(true)
    }
  };

  const handleCloseTagsModal = () => {
    setOpenTagsModal(false);

  };

  const handleNext = async () => {
    TrackGoogleAnalyticsEvent('Paso Completado', steps[activeStep].title)
    if (isLastStep) {
      handleOpenConfirmationModal();
    } else {
      next()
    }
  };

  const handleBack = () => {
    const backStep = activeStep - 1
    setActiveStep(backStep);
    onBack(activeStep);
  };

  const sourceActivities = contextValue.activities.map(activity => {
    if (activity.new) return { id: null, name: activity.name }
    return { id: activity.id, name: activity.name }
  });

  const handleStepForStepper = (step: number) => {
    if (step === activeStep) return
    if (disableStpperButton(step)) return dispatch(popSnackbar({
      type: 'warning',
      message: t('cantSkipStep'),
      duration: 5000
    }));;
    const ifNext = step > activeStep;
    if (ifNext) {
      setShowPreviousRequestModal(false);
      //The onNext needs to be called with the activeStep, because the activeStep is the current step, so we need to execute the onNext context function for the current step that we are leaving
      onNext(activeStep, true)
        .then(() => {
          setActiveStep(step);
        }).catch((e) => {
          console.log('cached error', e);
        });
    } else {
      onBack(step)
      setActiveStep(step);
    }
  };

  useEffect(() => {
    const checkIfPendingRequestNotChanged = (lastPendingRequests: EquivalencyRequest[], lastEndedRequests: EquivalencyRequest[], pendingRequests: EquivalencyRequest[], endedRequests: EquivalencyRequest[]) => {
      //This function checks if the pending requests have changed since the last time the user was in the target selection step, it cam have some other request or some other matchingActivities
      //In the case that changes wee need to setPreviousRequestFlag(false), and that clear the third step information
      if ((lastPendingRequests.length > 0 || lastEndedRequests.length > 0)
        && compareArraysByProperty(pendingRequests, lastPendingRequests, 'id')
        && compareArraysByProperty(endedRequests, lastEndedRequests, 'id')
        && continueAnyway
        && !pendingRequests.some((item: EquivalencyRequest) => item.matchingActivities && item.matchingActivities.length > 0)
        && !endedRequests.some((item: EquivalencyRequest) => item.matchingActivities && item.matchingActivities.length > 0)
      ) return true
      else return false
    }

    const fetchData = async () => {
      const input = {
        offset: 0,
        pageSize: 10,
        sourceSyllabusName: contextValue.source_syllabus?.id === '' ? contextValue.source_syllabus.name : undefined,
        sourceSyllabusId: contextValue.source_syllabus?.id === '' ? undefined : contextValue.source_syllabus?.id,
        sourceActivities: sourceActivities,
        targetSyllabusId: contextValue.target_syllabus?.id,
        // state: EquivalencyRequestStates.pending,
        documentType: contextValue.student?.document_type,
        documentNumber: contextValue.student?.document_number,
        generateToken
      }
      if (checkPendingRequests) {
        const lastPendingRequests = previousPendingRequestList
        const lastEndedRequests = previousEndedRequestList

        const result = await dispatch(searchPreviousEquivalencyRequest(input))
          .unwrap();
        if (result.count > 0) {
          const pendingRequests = result.items.filter((item: EquivalencyRequest) => item.state === EquivalencyRequestStates.pending)
          const endedRequests = result.items.filter((item: EquivalencyRequest) => isExternal ? item.state === EquivalencyRequestStates.aproved : (item.state === EquivalencyRequestStates.rejected || item.state === EquivalencyRequestStates.aproved))

          setPreviousPendingRequestList(pendingRequests)
          setPreviousEndedRequestList(endedRequests)

          const hasMatchingActivities = [...pendingRequests, ...endedRequests].some(
            (req) => req.matchingActivities && req.matchingActivities.length > 0
          );

          if (checkIfPendingRequestNotChanged(lastPendingRequests, lastEndedRequests, pendingRequests, endedRequests)) {
            setPreviousRequestFlag(true)
          } else {
            setContinueAnyway(false)
            if(hasMatchingActivities){
              setShowPreviousRequestModal(true);
              setPreviousRequestFlag(false)
            } else{
              setShowPreviousRequestModal(false);
              setPreviousRequestFlag(true)
            }      
          }
        }
        else {
          setPreviousPendingRequestList([])
          setPreviousEndedRequestList([])
          setPreviousRequestFlag(true)
        }
      } else if (isLastStep) {
        handleOpenConfirmationModal();
      }
    }
    if (activeStep === STEPS.TARGET_SELECTION && targetSyllabus) fetchData();
    if (!targetSyllabus) {
      setContinueAnyway(false)
      setPreviousRequestFlag(false)
    }

  }, [activeStep, targetSyllabus, continueAnyway]);

  useEffect(() => {
    const pendingIds = previousPendingRequestList.map(request => request.id);
    const endedIds = previousEndedRequestList.map(request => request.id);
    const allRequestIds = [...pendingIds, ...endedIds];

    for (const id of allRequestIds) {
      dispatch(fetchRequestById(id));
    }
  }, [previousPendingRequestList, previousEndedRequestList]);


  return (
    <>
      <Grid container
        direction='row'
        justifyContent="space-between"
        alignItems='center'
        width='100vw'
        padding='24px'
        height='100%'
      >
        <Grid alignContent='center'>
          {isInitialStep && welcomeScreen &&
            <Button
              fullWidth
              variant="contained"
              onClick={showWelcomeScreen}
              startIcon={<ArrowBackIosIcon fontSize="small" />}
              tabIndex={0}
              className="REQXXI-RequestStepper-Button"
            >
              <p> {t('backLabel')} </p>
            </Button>
          }
          {(!isInitialStep && !isFinalStep) &&
            <Button
              fullWidth
              variant="contained"
              onClick={handleBack}
              startIcon={<ArrowBackIosIcon fontSize="small" />}
              className="REQXXI-RequestStepper-Button"
              disabled={disbaleGoBack}
              tabIndex={0}
            >
              <p> {t('backLabel')} </p>
            </Button>
          }
        </Grid>
        <Grid alignContent='center'>
          <Stepper activeStep={activeStep} className='REQXXI-RequestStepper-StepperContainer'>
            {steps.map(({ key, completed }) => {
              if (key === STEPS.ACADEMIC_HISTORY && activeStep > key && (
                !allActivitiesWasEnriched
              ))
                return (
                  <Tooltip title={t('mandatoryDataNeeded')} key={key}>
                    <Step key={key} completed={false} onClick={() => handleStepForStepper(key)} >
                      <StepLabel className="REQXXI-RequestStepper-StepperLabel-Warning" />
                    </Step>
                  </Tooltip>
                )
              else
                return (
                  <Step key={key} completed={completed} onClick={() => handleStepForStepper(key)}>
                    <StepLabel className="REQXXI-RequestStepper-StepperLabel">
                    </StepLabel>
                  </Step>
                )
            })}
          </Stepper>
        </Grid>
        <Grid alignContent='center'>
          {!isFinalStep &&
            <Button
              fullWidth
              id='nextButton'
              data-testid='nextButton'
              variant="contained"
              onClick={handleNext}
              disabled={disableNextStep()}
              endIcon={<ArrowForwardIosIcon fontSize="small" />}
              className={'REQXXI-RequestStepper-Button'}
            >
              <p>{lastNextStepLabel}</p>
            </Button>}
        </Grid>
      </Grid>
      {
        showPreviousRequestModal && requestById &&
        <PreviousRequestModal tags={requestById.tags} pendingRequests={previousPendingRequestList} endedRequests={previousEndedRequestList} isOpen={showPreviousRequestModal} onClose={handleClosePreviousRequestModal} equivalencyRuleCheckEnabled={equivalencyRuleCheckEnabled} />
      }
      {
        equivalencyRuleCheckEnabled && openTagsModal && <TagsModal simulation={simulationData} isOpen={openTagsModal} onClose={handleCloseTagsModal} />
      }
      {showConfirmationModal && <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
        studentData={studentData}
        academicHistory={academicHistory}
        proposals={proposals}
        targetSyllabus={targetSyllabus}
        targetInstitution={targetInstitution}
        targetActivities={targetSyllabusActivities}
        cardinality={cardinality}
        allActivitiesWasEnriched={allActivitiesWasEnriched}
        termsAndConditionsChecked={termsAndConditionsChecked}
        hasStudyPlanAndAcademicReport={hasStudyPlanAndAcademicReport}
        thirdStepValidation={thirdStepValidation}
        handleStepForStepper={handleStepForStepper}
      />}
    </>
  );
};

export default RequestStepper;